import { useState } from "react";
import { whenReady } from "./hooks/tourReadiness";
import { scrollStepIntoView } from "./hooks/tourScroll";

function useColumnVisibilityTour() {
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    const steps = [
        {
            target: '.column-visibility-dialog',
            title: 'Column Visibility & Ordering',
            content: 'This dialog allows you to customize which columns are visible in your table and change their display order.',
            disableBeacon: true,
            placement: 'center',
        },
        {
            target: '.dialog-search input',
            title: 'Search Columns',
            content: 'Use this search box to quickly find specific columns by name.',
            disableBeacon: true,
        },
        {
            target: '.dialog-actions',
            title: 'Quick Actions',
            content: 'Use these buttons to quickly show or hide all columns at once.',
            disableBeacon: true,
        },
        {
            target: '.column-row:first-child .drag-handle',
            title: 'Drag Handle',
            content: 'Click and hold this handle to drag a column up/down to a new position.',
            disableBeacon: true,
        },
        {
            target: '.column-row:first-child .checkbox-container',
            title: 'Show/Hide Column',
            content: 'Check or uncheck this box to show or hide the column in your table view.',
            disableBeacon: true,
        },
        {
            target: '.dialog-footer',
            title: 'Save or Cancel',
            content: 'Click "Apply" to save your changes, or "Cancel" to discard them and close the dialog.',
            disableBeacon: true,
        },
    ];

    const handleStartTutorial = () => {
        setRunTour(false);
        setStepIndex(0);

        //WAIT FOR THE FIRST STEP'S TARGET, and only that one.
        //
        //What stood here filtered every step against the DOM, assigned the
        //result to `availableSteps`, tested it for emptiness and then handed
        //Joyride the UNFILTERED list — so the comment "filter out steps whose
        //elements don't exist" described work the function did not do, and the
        //only effect was an abort when not a single step resolved. Every step
        //but the first is recoverable by the target_not_found branch below; the
        //first is not recoverable at all. See tourReadiness.js.
        whenReady(steps[0]?.target, () => {
            setStepIndex(0);
            setRunTour(true);
        });
    };

    const handleJoyrideCallback = (data) => {
        const { status, action, type, index, origin, step } = data;
        //Missing the tooltip and hitting the dimmed backdrop is a mis-click,
        //not a decision to quit. Mirrors hooks/useSimpleTour.js.
        if (origin === 'overlay' && action === 'close') return;

        //This tour runs with `disableScrolling`, so bringing the target on
        //screen is our job — see hooks/tourScroll.js.
        if (type === 'step:before') scrollStepIntoView(step);


        //A step whose target never rendered. THE SIGNAL IS THE EVENT TYPE, not
        //the status: react-joyride declares STATUS.ERROR and never assigns it
        //anywhere (grep the dist bundle), so the `status === 'error'` this used
        //to test alone was false for every event Joyride has ever sent. It is
        //kept only as belt-and-braces.
        //
        //Missing the real branch is not a cosmetic bug. JoyrideStep.render
        //returns null unless the target is a live DOM element, and the overlay
        //hides itself at lifecycle "init" — so with no branch the tour renders
        //NOTHING: no tooltip, no spotlight, no Close button, while runTour stays
        //true at the stuck index. From the user's side the tour simply vanishes.
        //See hooks/useSimpleTour.js, which carries this reasoning for the tours
        //written on it.
        if (type === 'error:target_not_found' || status === 'error') {
            console.warn('Tour: target not found, skipping step', index);
            const nextIndex = index + 1;
            if (nextIndex < steps.length) {
                setStepIndex(nextIndex);
                return;
            } else {
                setRunTour(false);
                setStepIndex(0);
                return;
            }
        }

        // Handle all closing actions consistently
        if (status === 'finished' || status === 'skipped') {
            setRunTour(false);
            setStepIndex(0);
            return;
        }
        
        // Treat 'close' action the same as 'stop'
        if (action === 'close' || action === 'stop') {
            setRunTour(false);
            setStepIndex(0);
            return;
        }
        
        if (type === 'step:after' && action === 'next') {
            setStepIndex(index + 1);
        } else if (type === 'step:after' && action === 'prev') {
            let targetIndex = index - 1;

            if (targetIndex < 0) {
                setRunTour(false);
                setStepIndex(0);
                return;
            }

            setStepIndex(targetIndex);
        }
    };

    return {
        runTour,
        stepIndex,
        steps,
        handleStartTutorial,
        handleJoyrideCallback,
    };
}

export default useColumnVisibilityTour;